import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, ChevronLeft, ChevronRight, Printer, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Percent, Lock, FileCheck2, FileClock, Loader2,
  Pencil, Check, ReceiptText,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { financeApi } from "@/lib/unifiedApi";
import { storageService } from "@/lib/storage";
import { toast } from "sonner";
import {
  fmt, monthLabelFull, monthLabelEn, fmtDate, NO_CATEGORY, CAT_COLORS, INCOME_CATEGORIES, EXPENSE_CATEGORIES,
  WHT_RATE_OPTIONS, whtAmountFromRate, whtRateFromAmount,
  normalizeRows, buildMonths, catBreakdown, computeTaxSummary, taxParts, pnlBreakdown,
  canViewFinance, isFinanceAdmin, isPayrollCategory,
} from "@/lib/financeUtils";

// Horizontal category bars
function CatBars({ data, total }) {
  if (!data.length) return <p className="text-xs text-gray-400 py-4 text-center">ไม่มีข้อมูล</p>;
  const max = data[0].value || 1;
  return (
    <div className="space-y-2.5">
      {data.map((c, i) => (
        <div key={c.name}>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="flex items-center gap-1.5 text-gray-600 min-w-0">
              <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: c.name === NO_CATEGORY ? "#cbd5e1" : CAT_COLORS[i % CAT_COLORS.length] }} />
              <span className="truncate">{c.name}</span>
            </span>
            <span className="text-gray-500 flex-shrink-0 ml-2" style={{ fontVariantNumeric: "tabular-nums" }}>
              ฿{fmt(c.value)} <span className="text-gray-400">· {total > 0 ? Math.round((c.value / total) * 100) : 0}%</span>
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(c.value / max) * 100}%`, background: c.name === NO_CATEGORY ? "#cbd5e1" : CAT_COLORS[i % CAT_COLORS.length] }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FinanceMonthPage() {
  const { monthKey } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = isFinanceAdmin(user);
  const canView = canViewFinance(user);
  const restricted = canView && !isAdmin; // finance-staff (Plai): data entry only

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [editing, setEditing] = useState(false);

  const rowsRef = useRef([]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  useEffect(() => {
    if (!canView) { setLoading(false); return; }
    let active = true;
    financeApi.load()
      .then((doc) => { if (active && doc) setRows(normalizeRows(doc.rows)); })
      .catch((e) => console.error("[finance] load", e))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [canView]);

  // Edit a transaction and persist (shared singleton doc — same as the list page)
  const updateTxn = (id, patch) => {
    const next = rowsRef.current.map((t) => (t.id === id ? { ...t, ...patch } : t));
    rowsRef.current = next;
    setRows(next);
    return next;
  };
  const persistRows = async (arr) => {
    try { await financeApi.save({ rows: arr }); }
    catch (e) { console.error("[finance] save", e); toast.error("บันทึกไม่สำเร็จ"); }
  };
  const editTxn = (id, patch) => updateTxn(id, patch);          // on change (text)
  const saveTxn = (id, patch) => persistRows(updateTxn(id, patch)); // toggles/uploads
  const persistCurrent = () => persistRows(rowsRef.current);     // on blur
  const uploadReceipt = async (t, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("ไฟล์ใหญ่เกิน 10MB"); return; }
    setUploadingId(t.id);
    try {
      const res = await storageService.uploadFile(file, "finance/current");
      saveTxn(t.id, { receiptUrl: res.url, receiptName: res.name });
      toast.success("อัพโหลดใบเสร็จแล้ว");
    } catch (e) { console.error("[finance] upload", e); toast.error("อัพโหลดไม่สำเร็จ"); }
    finally { setUploadingId(null); }
  };

  const months = useMemo(() => buildMonths(rows), [rows]);
  const idx = months.findIndex((m) => m.key === monthKey);
  const m = idx >= 0 ? months[idx] : null;
  const prev = idx > 0 ? months[idx - 1] : null;
  const next = idx >= 0 && idx < months.length - 1 ? months[idx + 1] : null;

  const bd = useMemo(() => (m ? catBreakdown(m.txns) : { income: [], expense: [] }), [m]);
  const tax = useMemo(() => (m ? computeTaxSummary(m.txns) : null), [m]);
  // Finance-staff never see payroll rows (kept in the doc for saves)
  const displayTxns = m ? (restricted ? m.txns.filter((t) => !isPayrollCategory(t.category)) : m.txns) : [];

  if (!canView) {
    return (
      <div className="page-container">
        <div className="rounded-2xl bg-white border border-gray-200 py-20 flex flex-col items-center gap-3 mt-8">
          <Lock size={40} className="text-gray-300" />
          <p className="text-gray-500 font-medium">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#3b6ef5] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!m) {
    return (
      <div className="page-container">
        <button onClick={() => navigate("/finance")} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1.5 mb-6">
          <ArrowLeft size={16} /> กลับไปหน้า Finance
        </button>
        <div className="rounded-2xl bg-white border border-gray-200 py-16 text-center text-gray-400">
          ไม่มีข้อมูลของเดือนนี้
        </div>
      </div>
    );
  }

  const margin = m.income > 0 ? (m.net / m.income) * 100 : 0;
  const delta = (cur, before) => (before > 0 ? ((cur - before) / before) * 100 : null);
  const dIncome = prev ? delta(m.income, prev.income) : null;
  const dExpense = prev ? delta(m.expense, prev.expense) : null;

  const Mom = ({ v }) => {
    if (v == null) return <span className="text-[11px] text-gray-400">— เดือนแรก</span>;
    return <span className="text-[11px] text-gray-500">{v >= 0 ? "▲" : "▼"} {Math.abs(v).toFixed(0)}% จากเดือนก่อน</span>;
  };

  // ── Daily income/expense series for the printable P&L chart ──
  const [yy, mm] = m.key.split("-").map(Number);
  const daysInMonth = new Date(yy, mm, 0).getDate();
  const dayAgg = Array.from({ length: daysInMonth }, (_, i) => ({ day: i + 1, income: 0, expense: 0 }));
  for (const t of m.txns) {
    const d = Number((t.date || "").split("-")[2]);
    if (d >= 1 && d <= daysInMonth) { dayAgg[d - 1].income += Number(t.income) || 0; dayAgg[d - 1].expense += Number(t.expense) || 0; }
  }
  const maxDaily = Math.max(1, ...dayAgg.map((d) => Math.max(d.income, d.expense)));
  const CW = 600, CH = 150, cPadX = 8, cTop = 16;
  const cx = (day) => cPadX + (daysInMonth > 1 ? (day - 1) / (daysInMonth - 1) : 0) * (CW - 2 * cPadX);
  const cyv = (v) => CH - (v / maxDaily) * (CH - cTop);
  const incLine = dayAgg.map((d) => `${cx(d.day).toFixed(1)},${cyv(d.income).toFixed(1)}`).join(" ");
  const expLine = dayAgg.map((d) => `${cx(d.day).toFixed(1)},${cyv(d.expense).toFixed(1)}`).join(" ");
  const incArea = `${incLine} ${cx(daysInMonth).toFixed(1)},${CH} ${cx(1).toFixed(1)},${CH}`;
  const todayStr = new Date().toLocaleDateString("th-TH");
  // Accounting P&L figures — excluding VAT (company is VAT-registered)
  const pnl = pnlBreakdown(m.txns);

  return (
    <div className="page-container finance-report" data-testid="finance-month-page">
      <div className="screen-report">
      {/* Action bar (hidden in print) */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5" data-no-print>
        {!restricted ? (
          <button onClick={() => navigate("/finance")} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1.5">
            <ArrowLeft size={16} /> กลับ
          </button>
        ) : <span />}
        <div className="flex items-center gap-2">
          <button onClick={() => prev && navigate(`/finance/month/${prev.key}`)} disabled={!prev}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => next && navigate(`/finance/month/${next.key}`)} disabled={!next}
            className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 disabled:opacity-30">
            <ChevronRight size={16} />
          </button>
          <Button variant="outline" onClick={() => setEditing((e) => !e)}
            className={editing ? "border-emerald-300 text-emerald-700 hover:bg-emerald-50" : ""}>
            {editing ? <><Check size={15} className="mr-1.5" /> เสร็จสิ้น</> : <><Pencil size={15} className="mr-1.5" /> แก้ไขรายละเอียด</>}
          </Button>
          {!restricted && (
            <Button onClick={() => window.print()} className="bg-[#3b6ef5] hover:bg-[#3260e0] text-white">
              <Printer size={15} className="mr-1.5" /> Export PDF
            </Button>
          )}
        </div>
      </div>

      {/* Title */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">{restricted ? monthLabelEn(m.key) : `รายงานการเงิน — ${monthLabelEn(m.key)}`}</h1>
        <p className="text-gray-500 mt-1 text-sm">{restricted ? `${displayTxns.length} รายการ · กรอกรายละเอียดแต่ละรายการ` : `FLASHYOURMEME · ${m.count} รายการ · ออกรายงาน ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}</p>
      </div>

      {/* Summary numbers — hidden from finance-staff (Plai) */}
      {!restricted && (<>
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowUpRight size={16} className="text-emerald-600" /><p className="text-xs text-gray-500">รายรับ</p></div>
          <p className="text-2xl font-extrabold text-emerald-600" style={{ fontVariantNumeric: "tabular-nums" }}>฿{fmt(m.income)}</p>
          <Mom v={dIncome} />
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1"><ArrowDownRight size={16} className="text-red-600" /><p className="text-xs text-gray-500">รายจ่าย</p></div>
          <p className="text-2xl font-extrabold text-red-600" style={{ fontVariantNumeric: "tabular-nums" }}>฿{fmt(m.expense)}</p>
          <Mom v={dExpense} />
        </div>
        <div className="rounded-2xl border p-4" style={{ background: m.net >= 0 ? "#f0fdf4" : "#fef2f2", borderColor: m.net >= 0 ? "#bbf7d0" : "#fecaca" }}>
          <div className="flex items-center gap-2 mb-1">
            {m.net >= 0 ? <TrendingUp size={16} className="text-emerald-600" /> : <TrendingDown size={16} className="text-red-600" />}
            <p className="text-xs" style={{ color: m.net >= 0 ? "#16a34a" : "#dc2626" }}>{m.net >= 0 ? "กำไรสุทธิ" : "ขาดทุนสุทธิ"}</p>
          </div>
          <p className="text-2xl font-extrabold" style={{ color: m.net >= 0 ? "#16a34a" : "#dc2626", fontVariantNumeric: "tabular-nums" }}>{m.net >= 0 ? "+" : "−"}฿{fmt(Math.abs(m.net))}</p>
          <span className="text-[11px]" style={{ color: m.net >= 0 ? "#16a34a" : "#dc2626" }}>margin {margin.toFixed(1)}% · ยอดสิ้นเดือน ฿{m.closingBalance != null ? fmt(m.closingBalance) : "—"}</span>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">รายจ่ายตามหมวดหมู่</p>
            <p className="text-sm font-bold text-red-600">฿{fmt(m.expense)}</p>
          </div>
          <CatBars data={bd.expense} total={m.expense} />
        </div>
        <div className="rounded-2xl bg-white border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700">รายรับตามหมวดหมู่</p>
            <p className="text-sm font-bold text-emerald-600">฿{fmt(m.income)}</p>
          </div>
          <CatBars data={bd.income} total={m.income} />
        </div>
      </div>

      {/* Tax summary */}
      {tax && tax.tagged > 0 && (
        <div className="rounded-2xl bg-white border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5"><Percent size={15} className="text-[#3b6ef5]" /> สรุปภาษี</p>
            {tax.invoicePending > 0 && (
              <span className="text-[11px] font-medium text-[#2563eb] flex items-center gap-1">
                <ReceiptText size={13} /> {tax.invoicePending} รายการยังไม่ส่งใบกำกับภาษี
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5">
            <div className="rounded-xl bg-gray-50 px-3 py-2.5"><p className="text-[11px] text-gray-500">VAT ซื้อ (ขอคืน)</p><p className="text-base font-bold text-gray-900">฿{fmt(tax.vatInput)}</p></div>
            <div className="rounded-xl bg-gray-50 px-3 py-2.5"><p className="text-[11px] text-gray-500">VAT ขาย (นำส่ง)</p><p className="text-base font-bold text-gray-900">฿{fmt(tax.vatOutput)}</p></div>
            <div className="rounded-xl px-3 py-2.5" style={{ background: tax.vatNet >= 0 ? "#fef2f2" : "#f0fdf4" }}><p className="text-[11px]" style={{ color: tax.vatNet >= 0 ? "#dc2626" : "#16a34a" }}>VAT สุทธิ {tax.vatNet >= 0 ? "(ต้องจ่าย)" : "(ขอคืน)"}</p><p className="text-base font-bold" style={{ color: tax.vatNet >= 0 ? "#dc2626" : "#16a34a" }}>฿{fmt(Math.abs(tax.vatNet))}</p></div>
            <div className="rounded-xl bg-gray-50 px-3 py-2.5"><p className="text-[11px] text-gray-500">WHT เราหักไว้</p><p className="text-base font-bold text-gray-900">฿{fmt(tax.whtWithheld)}</p></div>
            <div className="rounded-xl bg-gray-50 px-3 py-2.5"><p className="text-[11px] text-gray-500">WHT ถูกหัก (เครดิต)</p><p className="text-base font-bold text-gray-900">฿{fmt(tax.whtDeducted)}</p></div>
          </div>
        </div>
      )}
      </>)}

      {/* Category datalists for the editable cells */}
      <datalist id="fin-cat-income">{INCOME_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="fin-cat-expense">{EXPENSE_CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
      <datalist id="fin-wht-rates">{WHT_RATE_OPTIONS.map((r) => <option key={r} value={r} />)}</datalist>

      {/* Transaction detail (editable) */}
      <div className="rounded-2xl bg-white border border-gray-200 overflow-hidden">
        <p className="text-sm font-semibold text-gray-700 px-4 py-3 border-b border-gray-100">รายละเอียดรายการ ({displayTxns.length})</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{ borderCollapse: "collapse", minWidth: editing ? 940 : 680 }}>
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-[10px] uppercase">
                <th className="text-left font-semibold px-3 py-2">วันที่</th>
                <th className="text-left font-semibold px-3 py-2">รายละเอียด</th>
                <th className="text-left font-semibold px-3 py-2">หมวดหมู่</th>
                <th className="text-left font-semibold px-3 py-2">ภาษี</th>
                <th className="text-right font-semibold px-3 py-2">รายรับ</th>
                <th className="text-right font-semibold px-3 py-2">รายจ่าย</th>
                <th className="text-center font-semibold px-3 py-2">ใบเสร็จ</th>
              </tr>
            </thead>
            <tbody style={{ fontVariantNumeric: "tabular-nums" }}>
              {displayTxns.map((t) => {
                const isIncome = Number(t.income) > 0;
                const p = taxParts(t);
                return (
                  <tr key={t.id} className="border-t border-gray-100 align-top">
                    <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(t.date)}</td>

                    {/* รายละเอียด */}
                    <td className="px-3 py-2" style={{ minWidth: editing ? 200 : 160 }}>
                      {editing ? (
                        <input
                          value={t.label || ""}
                          onChange={(e) => editTxn(t.id, { label: e.target.value })}
                          onBlur={persistCurrent}
                          placeholder={isIncome ? "รายละเอียดรายรับ..." : "รายละเอียดรายจ่าย..."}
                          className="w-full px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-700 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                        />
                      ) : (
                        <div className="font-medium text-gray-900">{t.label || <span className="text-gray-400">{t.desc || "-"}</span>}</div>
                      )}
                      {t.desc && (t.label || editing) && <div className="text-[10px] text-gray-400 truncate max-w-[240px] mt-0.5">{t.desc}</div>}
                    </td>

                    {/* หมวดหมู่ */}
                    <td className="px-3 py-2" style={{ minWidth: editing ? 130 : 90 }}>
                      {editing ? (
                        <input
                          value={t.category || ""}
                          list={isIncome ? "fin-cat-income" : "fin-cat-expense"}
                          onChange={(e) => editTxn(t.id, { category: e.target.value })}
                          onBlur={persistCurrent}
                          placeholder="หมวดหมู่..."
                          className="w-full px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-600 text-xs focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                        />
                      ) : (
                        <span className="text-gray-600">{t.category || <span className="text-gray-300">—</span>}</span>
                      )}
                    </td>

                    {/* ภาษี */}
                    <td className="px-3 py-2" style={{ minWidth: editing ? 180 : 120 }}>
                      {editing ? (
                        <>
                          <div className="flex items-center gap-2 flex-wrap">
                            <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                              <input type="checkbox" checked={!!t.hasVat} onChange={(e) => saveTxn(t.id, { hasVat: e.target.checked, vatTouched: true })} className="w-3.5 h-3.5 accent-[#3b6ef5]" /> VAT
                            </label>
                            {isIncome && (
                              <label
                                title={t.taxInvoiceSent ? "ส่งใบกำกับภาษีให้ลูกค้าแล้ว" : "ยังไม่ได้ส่งใบกำกับภาษี"}
                                className={`flex items-center gap-1 text-[10px] cursor-pointer ${t.taxInvoiceSent ? "text-[#2563eb] font-medium" : "text-blue-300 hover:text-blue-500"}`}
                              >
                                <input type="checkbox" checked={!!t.taxInvoiceSent} onChange={(e) => saveTxn(t.id, { taxInvoiceSent: e.target.checked })} className="hidden" />
                                <ReceiptText size={13} /> ใบกำกับ
                              </label>
                            )}
                            <label className="flex items-center gap-1 text-[10px] text-gray-600 cursor-pointer">
                              <input type="checkbox" checked={!!t.wht} onChange={(e) => saveTxn(t.id, { wht: e.target.checked })} className="w-3.5 h-3.5 accent-[#3b6ef5]" /> {isIncome ? "ถูกหัก" : "หัก"}
                            </label>
                            {t.wht && (
                              <>
                                <span className="inline-flex items-center gap-0.5">
                                  <input
                                    type="number" min="0" max="100" step="0.5" list="fin-wht-rates"
                                    value={t.whtRate ?? ""}
                                    onChange={(e) => { const r = e.target.value === "" ? 0 : Number(e.target.value); editTxn(t.id, { whtRate: r, whtAmount: whtAmountFromRate(t, r) }); }}
                                    onBlur={persistCurrent}
                                    placeholder="%"
                                    className="w-10 px-1 py-0.5 rounded border border-gray-200 bg-white text-[10px] text-gray-600 text-right focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                                  />
                                  <span className="text-[9px] text-gray-400">%</span>
                                </span>
                                <span className="inline-flex items-center gap-0.5">
                                  <span className="text-[9px] text-gray-400">฿</span>
                                  <input
                                    type="number" min="0" step="0.01"
                                    value={t.whtAmount ?? ""}
                                    onChange={(e) => { const a = e.target.value === "" ? 0 : Number(e.target.value); editTxn(t.id, { whtAmount: a, whtRate: whtRateFromAmount(t, a) }); }}
                                    onBlur={persistCurrent}
                                    placeholder="ยอดหัก"
                                    className="w-16 px-1 py-0.5 rounded border border-gray-200 bg-white text-[10px] text-gray-600 text-right focus:outline-none focus:ring-1 focus:ring-[#3b6ef5]"
                                  />
                                </span>
                              </>
                            )}
                          </div>
                          {(t.hasVat || t.wht) && (
                            <div className="text-[9px] text-gray-400 mt-1">ฐาน ฿{fmt(p.base)}{t.hasVat ? ` · VAT ฿${fmt(p.vat)}` : ""}{t.wht ? ` · หัก ฿${fmt(p.wht)}` : ""}</div>
                          )}
                        </>
                      ) : (
                        <div className="flex flex-wrap gap-1 items-center">
                          {t.hasVat && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">VAT {fmt(p.vat)}</span>}
                          {t.wht && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-50 text-red-600">หัก ฿{fmt(p.wht)}</span>}
                          {isIncome && t.hasVat && (
                            <span title={t.taxInvoiceSent ? "ส่งใบกำกับภาษีแล้ว" : "ยังไม่ได้ส่งใบกำกับภาษี"}
                              className={`inline-flex items-center ${t.taxInvoiceSent ? "text-[#2563eb]" : "text-blue-200"}`}>
                              <ReceiptText size={12} />
                            </span>
                          )}
                          {!t.hasVat && !t.wht && <span className="text-gray-300">—</span>}
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-2 text-right text-emerald-600 font-medium">{isIncome ? fmt(t.income) : ""}</td>
                    <td className="px-3 py-2 text-right text-red-600 font-medium">{!isIncome ? fmt(t.expense) : ""}</td>

                    {/* ใบเสร็จ */}
                    <td className="px-3 py-2 text-center">
                      {uploadingId === t.id ? (
                        <Loader2 size={14} className="animate-spin text-gray-400 inline" />
                      ) : t.receiptUrl ? (
                        <span className="inline-flex items-center gap-1.5">
                          <a href={t.receiptUrl} target="_blank" rel="noopener noreferrer" title="ดูใบเสร็จ" className="text-emerald-600"><FileCheck2 size={16} /></a>
                          {editing && (
                            <label className="text-[10px] text-gray-400 hover:text-gray-600 cursor-pointer" data-no-print>
                              <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(t, e.target.files?.[0])} />เปลี่ยน
                            </label>
                          )}
                        </span>
                      ) : editing ? (
                        <label
                          title={(t.hasVat || t.wht) ? "รอเอกสาร — คลิกเพื่ออัพโหลดใบเสร็จ/ใบกำกับภาษี" : "อัพโหลดใบเสร็จ"}
                          className={`cursor-pointer inline-flex ${(t.hasVat || t.wht) ? "text-amber-500 hover:text-amber-600" : "text-gray-300 hover:text-gray-500"}`}
                        >
                          <input type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadReceipt(t, e.target.files?.[0])} />
                          <FileClock size={16} />
                        </label>
                      ) : (
                        <FileClock size={16} className={`inline ${(t.hasVat || t.wht) ? "text-amber-500" : "text-gray-300"}`}
                          title={(t.hasVat || t.wht) ? "รอเอกสาร (ยังไม่อัพใบเสร็จ)" : "ไม่มีใบเสร็จ"} />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {!restricted && (
            <tfoot>
              <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold text-gray-900">
                <td className="px-3 py-2.5" colSpan={4}>รวม</td>
                <td className="px-3 py-2.5 text-right text-emerald-600">{fmt(m.income)}</td>
                <td className="px-3 py-2.5 text-right text-red-600">{fmt(m.expense)}</td>
                <td className="px-3 py-2.5" />
              </tr>
            </tfoot>
            )}
          </table>
        </div>
      </div>
      </div>{/* /screen-report */}

      {/* Printable P&L statement (Export PDF) — admin only, print-only */}
      {!restricted && (
        <div className="pnl-statement" style={{ color: "#1e293b", fontSize: 12.5, lineHeight: 1.55 }}>
          {/* Header */}
          <div style={{ textAlign: "center", borderBottom: "2px solid #1e293b", paddingBottom: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: ".04em" }}>FLASHYOURMEME</div>
            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 5 }}>งบกำไรขาดทุน · Profit &amp; Loss Statement</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>สำหรับเดือน {monthLabelFull(m.key)}</div>
            <div style={{ fontSize: 9.5, color: "#94a3b8", marginTop: 2 }}>หน่วย: บาท · ยอดไม่รวมภาษีมูลค่าเพิ่ม (Excluding VAT)</div>
          </div>

          {/* Daily income vs expense line chart (style 3) */}
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <p style={{ fontSize: 11, fontWeight: 700, color: "#475569", margin: 0 }}>รายรับ–รายจ่าย รายวัน</p>
              <div style={{ display: "flex", gap: 14, fontSize: 10, color: "#475569" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, borderTop: "2.5px solid #111827" }} /> รายรับ</span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><span style={{ width: 14, borderTop: "2.5px solid #9ca3af" }} /> รายจ่าย</span>
              </div>
            </div>
            <svg viewBox={`0 0 ${CW} ${CH}`} style={{ width: "100%", height: 150, display: "block" }}>
              <defs>
                <linearGradient id="pnlInc" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#111827" stopOpacity="0.16" />
                  <stop offset="1" stopColor="#111827" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g stroke="#eef2f6" strokeWidth="1">
                <line x1="0" y1={CH * 0.25} x2={CW} y2={CH * 0.25} />
                <line x1="0" y1={CH * 0.5} x2={CW} y2={CH * 0.5} />
                <line x1="0" y1={CH * 0.75} x2={CW} y2={CH * 0.75} />
              </g>
              <polygon fill="url(#pnlInc)" points={incArea} />
              <polyline fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinejoin="round" points={expLine} />
              <polyline fill="none" stroke="#111827" strokeWidth="2.25" strokeLinejoin="round" points={incLine} />
            </svg>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8.5, color: "#94a3b8", marginTop: 3 }}>
              <span>1</span><span>{Math.round(daysInMonth / 4)}</span><span>{Math.round(daysInMonth / 2)}</span><span>{Math.round((daysInMonth * 3) / 4)}</span><span>{daysInMonth}</span>
            </div>
          </div>

          {/* Revenue */}
          <div style={{ fontWeight: 700, fontSize: 11, color: "#475569", margin: "10px 0 4px" }}>รายได้ (Revenue)</div>
          {pnl.income.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 11 }}>—</div> : pnl.income.map((c) => (
            <div key={c.name} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>{c.name}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.value)}</span>
            </div>
          ))}
          <div style={{ height: 1, background: "#e2e8f0", margin: "7px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <span>รวมรายได้</span><span style={{ color: "#16a34a", fontVariantNumeric: "tabular-nums" }}>{fmt(pnl.incomeTotal)}</span>
          </div>

          {/* Expenses */}
          <div style={{ fontWeight: 700, fontSize: 11, color: "#475569", margin: "14px 0 4px" }}>ค่าใช้จ่าย (Expenses)</div>
          {pnl.expense.length === 0 ? <div style={{ color: "#94a3b8", fontSize: 11 }}>—</div> : pnl.expense.map((c) => (
            <div key={c.name} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
              <span>{c.name}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(c.value)}</span>
            </div>
          ))}
          <div style={{ height: 1, background: "#e2e8f0", margin: "7px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700 }}>
            <span>รวมค่าใช้จ่าย</span><span style={{ color: "#dc2626", fontVariantNumeric: "tabular-nums" }}>{fmt(pnl.expenseTotal)}</span>
          </div>

          {/* Net */}
          <div style={{ borderTop: "2px double #334155", margin: "10px 0" }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 14 }}>
            <span>กำไร (ขาดทุน) สุทธิ</span>
            <span style={{ color: pnl.net >= 0 ? "#16a34a" : "#dc2626", fontVariantNumeric: "tabular-nums" }}>{pnl.net >= 0 ? "" : "−"}{fmt(Math.abs(pnl.net))}</span>
          </div>

          {/* VAT memo — not part of P&L, shown for reference */}
          <div style={{ marginTop: 14, padding: "8px 10px", background: "#f8fafc", border: "1px solid #eef2f6", borderRadius: 4, fontSize: 10, color: "#64748b" }}>
            <div style={{ fontWeight: 700, marginBottom: 3, color: "#475569" }}>หมายเหตุ — ภาษีมูลค่าเพิ่ม (ไม่ถือเป็นรายได้/ค่าใช้จ่าย)</div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>VAT ขาย (เก็บจากลูกค้า)</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(pnl.vatOutput)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between" }}><span>VAT ซื้อ (ขอคืนได้)</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(pnl.vatInput)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, color: "#334155", borderTop: "1px solid #e2e8f0", marginTop: 3, paddingTop: 3 }}>
              <span>VAT สุทธิ {pnl.vatOutput - pnl.vatInput >= 0 ? "(ต้องนำส่ง)" : "(ขอคืน)"}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(Math.abs(pnl.vatOutput - pnl.vatInput))}</span>
            </div>
          </div>

          <div style={{ marginTop: 18, paddingTop: 12, borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", fontSize: 9, color: "#94a3b8" }}>
            <span>FLASHYOURMEME Co., Ltd. · {m.count} รายการ</span><span>ออกรายงาน {todayStr}</span>
          </div>
        </div>
      )}
    </div>
  );
}
